import { useState } from "react";
import { PageHeader } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { StatusBadge } from "../components/StatusBadge";
import { CheckCircle2, Circle, Plus, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { AGENTES, agentePorId } from "../data/mockData";
import { fechaCorta } from "../lib/format";
import { useTasks } from "../context/TasksContext";

const hoyISO = () => new Date().toISOString().slice(0, 10);

export default function Tareas() {
  const { tareas, addTarea, toggleTarea } = useTasks();
  const [filtro, setFiltro] = useState("todos");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ titulo: "", agenteId: "ag-1", vence: hoyISO(), prioridad: "Media" });

  const lista = tareas.filter((t) => filtro === "todos" || t.agenteId === filtro);
  const pendientes = lista.filter((t) => !t.hecha);
  const hechas = lista.filter((t) => t.hecha);

  const crear = () => {
    if (!form.titulo.trim()) {
      toast.error("Poné un título para la tarea.");
      return;
    }
    addTarea({ titulo: form.titulo, agenteId: form.agenteId, vence: new Date(form.vence).toISOString(), prioridad: form.prioridad });
    setOpen(false);
    setForm({ titulo: "", agenteId: "ag-1", vence: hoyISO(), prioridad: "Media" });
    toast.success(`Tarea asignada a ${agentePorId(form.agenteId)?.nombre}.`);
  };

  return (
    <div>
      <PageHeader
        titulo="Tareas del equipo"
        descripcion="Asigná tareas a cada vendedor con vencimiento y prioridad, y seguí el avance del equipo."
      >
        <Select value={filtro} onValueChange={setFiltro}>
          <SelectTrigger data-testid="tareas-filter" className="w-[180px] rounded-md"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los vendedores</SelectItem>
            {AGENTES.map((a) => <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button className="btn-brand rounded-md" data-testid="tareas-new-button" onClick={() => setOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> Nueva tarea
        </Button>
      </PageHeader>

      <div className="space-y-6">
        <Section titulo={`Pendientes (${pendientes.length})`} tareas={pendientes} toggleTarea={toggleTarea} />
        {hechas.length > 0 && <Section titulo={`Hechas (${hechas.length})`} tareas={hechas} toggleTarea={toggleTarea} />}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="tareas-dialog">
          <DialogHeader><DialogTitle>Nueva tarea</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Tarea</Label>
              <Input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Ej: Llamar al propietario de..." className="rounded-md mt-1" data-testid="tareas-titulo" />
            </div>
            <div>
              <Label className="text-xs">Asignar a</Label>
              <Select value={form.agenteId} onValueChange={(v) => setForm({ ...form, agenteId: v })}>
                <SelectTrigger className="rounded-md mt-1" data-testid="tareas-agente"><SelectValue /></SelectTrigger>
                <SelectContent>{AGENTES.map((a) => <SelectItem key={a.id} value={a.id}>{a.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Vence</Label>
                <Input type="date" value={form.vence} onChange={(e) => setForm({ ...form, vence: e.target.value })} className="rounded-md mt-1" />
              </div>
              <div>
                <Label className="text-xs">Prioridad</Label>
                <Select value={form.prioridad} onValueChange={(v) => setForm({ ...form, prioridad: v })}>
                  <SelectTrigger className="rounded-md mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Alta">Alta</SelectItem>
                    <SelectItem value="Media">Media</SelectItem>
                    <SelectItem value="Baja">Baja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="btn-brand rounded-md" data-testid="tareas-submit" onClick={crear}>Asignar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ titulo, tareas, toggleTarea }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-900 mb-3">{titulo}</h3>
      {tareas.length === 0 ? (
        <Card className="p-5 rounded-lg border-slate-200 shadow-sm text-center text-sm text-slate-400">No hay tareas acá.</Card>
      ) : (
        <div className="space-y-2">
          {tareas.map((t) => {
            const ag = agentePorId(t.agenteId);
            const iniciales = ag?.nombre.split(" ").map((p) => p[0]).join("").slice(0, 2);
            return (
              <Card key={t.id} data-testid={`tarea-${t.id}`} className="flex items-center gap-3 p-3 rounded-lg border-slate-200 shadow-sm">
                <button onClick={() => toggleTarea(t.id)} data-testid={`tarea-toggle-${t.id}`} className="shrink-0">
                  {t.hecha ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <Circle className="h-5 w-5 text-slate-300 hover:text-slate-400" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${t.hecha ? "text-slate-400 line-through" : "text-slate-800 font-medium"}`}>{t.titulo}</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5"><CalendarClock className="h-3 w-3" /> Vence {fechaCorta(t.vence)}</p>
                </div>
                <StatusBadge estado={t.prioridad} />
                <div className="flex items-center gap-1.5 shrink-0" title={ag?.nombre}>
                  <span className="flex h-7 w-7 items-center justify-center rounded-md text-[11px] font-bold text-white" style={{ backgroundColor: ag?.color }}>{iniciales}</span>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
